// src/utils/mediaUnlock.ts
import { log } from "./debug";
import { AudioController } from "@/utils/audioController";
export type UnlockSource = 'gate' | 'reel-gate' | 'reel-play-button' | 'render' | 'first-gesture';

export interface MediaUnlockEventDetail {
  source: UnlockSource;
  audioContext: AudioContext | null;
}

// Lightweight debug logger (opt-in via window.__SC_DEBUG_AUTOPLAY)
function dlog(...args: any[]) {
  try { if ((window as any).__SC_DEBUG_AUTOPLAY) log("[AUTO]", ...args); } catch {}
}

// ---- Visibility helpers: only the top-most visible section may produce audio ----
function intersectionArea(rect: DOMRect, vw: number, vh: number): number {
  const x1 = Math.max(0, rect.left);
  const y1 = Math.max(0, rect.top);
  const x2 = Math.min(vw, rect.right);
  const y2 = Math.min(vh, rect.bottom);
  const w = Math.max(0, x2 - x1);
  const h = Math.max(0, y2 - y1);
  return w * h;
}
function computeTopVisibleSection(thresholdRatio = 0.15): HTMLElement | null {
  const sections = Array.from(document.querySelectorAll<HTMLElement>("section"));
  if (!sections.length) return null;

  const vw = window.innerWidth || document.documentElement.clientWidth || 0;
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  let top: HTMLElement | null = null;
  let best = 0;

  for (const s of sections) {
    const a = intersectionArea(s.getBoundingClientRect(), vw, vh);
    if (a > best) {
      best = a;
      top = s;
    }
  }

  const minArea = vw * vh * thresholdRatio;
  if (best >= minArea && top) {
    return top;
  }

  // Fallback: ScrollTrigger marks active sections with .visible — honor that if area fallback fails.
  const visibleFallback =
    sections.find((s) => s.classList.contains("visible")) || null;
  return visibleFallback;
}
export function isTopVisibleSection(el: Element | null): boolean {
  if (!el) return false;
  const sec = el.closest('section') as HTMLElement | null;
  if (!sec) return false;
  return computeTopVisibleSection() === sec;
}

class MediaUnlocker {
  constructor() {
    try {
      this.attachFirstGestureUnlock();
    } catch (err) {
      try { console.warn("[MediaUnlock] Failed to auto‑attach first gesture unlock", err); } catch {}
    }
  }
  private unlocked = false;
  private listenerAttached = false;
  private ctx: AudioContext | null = null;
  private firstGestureHandler?: (ev: Event) => void | Promise<void>;
  private globalPlayGuardAttached = false;

  get isUnlocked() { return this.unlocked; }
  get audioContext() { return this.ctx; }

  /** Force-unlock inside a known user gesture (e.g., from the audio gate). */
  async unlockNow(source: UnlockSource = 'gate'): Promise<void> {
    // If we've already successfully unlocked, don't repeat.
    if (this.unlocked) {
      dlog("unlock:skip (already unlocked)", { source });
      return;
    }

    dlog("unlock:start", { source });

    // 🔹 Prime background-style videos so they're safe to interact with.
    // We do NOT attempt to start WebAudio here anymore; HTMLMedia will handle audio
    // as long as play() is driven by a real user gesture via HUD/gates.
    const bgSelector = "video[data-role='background'], video[data-bg='true'], video[data-role='bg']";
    this.primeExistingVideos(bgSelector, { autoPlay: false });
    try {
      dlog("unlock:primed", { videos: document.querySelectorAll(bgSelector).length });
    } catch {}

    // Mark as unlocked at the app level so HUD/observers can trust audio is allowed.
    this.unlocked = true;
    try { AudioController.setAudioAllowed(true); } catch {}

    // Let the rest of the app know that audio is now allowed and should not be globally muted.
    try {
      AudioController.setMuted(false, "system");
      dlog("unlock:allow audio (no WebAudio context required)");
    } catch {}

    // We no longer try to auto-play any videos here; that must be driven
    // explicitly by HUD or reel controls in response to user actions.

    this.detachFirstGestureListeners();
    dlog("unlock:done", { source });
    this.emitUnlocked(source);
  }

  /** If no gate runs, attach a one-time unlock on first user gesture. */
  attachFirstGestureUnlock(): void {
    if (this.unlocked || this.listenerAttached) return;
    this.listenerAttached = true;
    dlog("unlock:attach-first-gesture");

    this.firstGestureHandler = async () => {
      dlog("unlock:first-gesture");
      await this.unlockNow('first-gesture');
    };

    const h = this.firstGestureHandler as EventListener;
    // Use bubble phase so more specific handlers (like HUD play) see the event naturally first.
    // Also avoid pointerdown here so "tap to wake HUD" doesn't accidentally trigger unlock.
    window.addEventListener('click',      h, { once: true });
    window.addEventListener('keydown',    h, { once: true });
    window.addEventListener('touchstart', h, { once: true });
  }

  detachFirstGestureListeners(): void {
    if (!this.listenerAttached) return;
    const h = this.firstGestureHandler as EventListener | undefined;
    if (h) {
      window.removeEventListener('keydown',    h, true);
      window.removeEventListener('touchstart', h, true);
      window.removeEventListener('click',      h, true);
    }
    dlog("unlock:detach-first-gesture");
    this.firstGestureHandler = undefined;
    this.listenerAttached = false;
  }

  /** Helpful to call after you render a section with background videos. */
  primeExistingVideos(selector = 'video', opts?: { autoPlay?: boolean }): void {
    const vids = Array.from(document.querySelectorAll<HTMLVideoElement>(selector));
    // One-time global guard: if ANY video tries to play while its section is not top-visible,
    // immediately pause+mute it. This prevents later reels from stealing audio during section-0.
    if (!this.globalPlayGuardAttached) {
      const globalPlayGuard = (ev: Event) => {
        try {
          const target = ev.target as EventTarget | null;
          if (!target || !(target instanceof HTMLVideoElement)) return;
          // Do not guard reels; their HUD/autoplay logic manages visibility.
          if (target.dataset.role === "reel" || target.dataset.reel === "true") return;
          const sec = target.closest('section');
          if (!isTopVisibleSection(sec) && !target.dataset.userAllowed) {
            target.muted = true;
            target.setAttribute('muted','');
            if (!target.paused) target.pause();
            dlog('global-play-guard: paused off-screen video', { src: target.currentSrc || target.getAttribute('src') });
          }
        } catch {}
      };
      window.addEventListener('play', globalPlayGuard as EventListener, true);
      this.globalPlayGuardAttached = true;
      dlog('unlock:installed global play guard');
    }
    vids.forEach(v => {
      if ((v as any).dataset?.scPrimed === "1") return;
      // Always ensure muted property + attribute are in sync before any play attempts
      v.muted = true; // reels can explicitly unmute later via HUD/gate
      v.setAttribute('muted','');
      (v as any).playsInline = true;
      v.setAttribute('playsinline','');
      v.setAttribute('webkit-playsinline','');

      // Respect loop intent
      const truthy = /^(1|true|on|yes)$/i;
      const falsy  = /^(0|false|off|no)$/i;

      const hasLoopAttr = v.hasAttribute('loop');
      const loopAttrVal = v.getAttribute('loop');
      const dataLoopVal = v.dataset.loop ?? null;

      const loopFromAttr = hasLoopAttr
        ? (
            loopAttrVal == null || loopAttrVal === ''
              ? true
              : truthy.test(loopAttrVal)
                ? true
                : falsy.test(loopAttrVal)
                  ? false
                  : true
          )
        : undefined;

      const loopFromData = dataLoopVal != null
        ? (truthy.test(dataLoopVal) ? true
           : falsy.test(dataLoopVal) ? false
           : undefined)
        : undefined;

      if (loopFromAttr !== undefined || loopFromData !== undefined) {
        v.loop = (loopFromData ?? loopFromAttr)!;
      } else if (v.dataset.role === 'background' || v.dataset.bg === 'true') {
        v.loop = true;
      }

      if (!v.preload) v.preload = 'metadata';

      v.addEventListener('canplay', () => {
        try { if (v.currentTime === 0) v.currentTime = 0.001; } catch {}
      }, { once: true });

      try { (v as any).dataset.scPrimed = "1"; } catch {}
      dlog("unlock:prime", { src: v.currentSrc || v.getAttribute('src'), loop: v.loop, role: v.dataset.role, bg: v.dataset.bg });

      // Gate audio & playback by visibility: off-screen videos stay muted/paused
      try {
        const sec = v.closest('section');
        if (!isTopVisibleSection(sec) && !v.dataset.userAllowed) {
          dlog('prime: pausing muted off-screen video', { src: v.currentSrc });
          v.muted = true;
          v.setAttribute('muted','');
          if (!v.paused) v.pause();
        }
      } catch {}

      // Defensive: if an off-screen video starts playback, pause it immediately
      v.addEventListener('play', () => {
        try {
          if (!v.dataset.userAllowed && !isTopVisibleSection(v.closest('section'))) {
            v.muted = true;
            if (!v.paused) v.pause();
          }
        } catch {}
      }, { capture: true });

      if (opts?.autoPlay) {
        v.play().catch(() => {});
      }
    });
  }

  private async tryWebAudio(): Promise<AudioContext | null> {
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      const ctx: AudioContext = new AC();
      await ctx.resume();
      return ctx;
    } catch {
      return null;
    }
  }

  private emitUnlocked(source: UnlockSource) {
    try {
      const detail: MediaUnlockEventDetail = { source, audioContext: this.ctx };
      window.dispatchEvent(new CustomEvent<MediaUnlockEventDetail>('sc:media-unlocked', { detail }));
      // 🔹 New event for UI convenience
      window.dispatchEvent(new Event('sc:audio-enabled'));
    } catch {}

    // Expose a simple global flag for other subsystems.
    (window as any).__storycore_audio = { enabled: this.unlocked, ctx: this.ctx };
  }
}

// Convenience named exports that proxy to the singleton, plus a default export.
export async function unlockNow(source: UnlockSource = 'gate'): Promise<void> {
  return MediaUnlock.unlockNow(source);
}

export function attachFirstGestureUnlock(): void {
  return MediaUnlock.attachFirstGestureUnlock();
}

export function detachFirstGestureUnlock(): void {
  return MediaUnlock.detachFirstGestureListeners();
}

export function primeExistingVideos(selector: string = 'video', opts?: { autoPlay?: boolean }): void {
  return MediaUnlock.primeExistingVideos(selector, opts);
}

export const MediaUnlock = new MediaUnlocker();

export function subscribeToMediaUnlocked(cb: (detail: MediaUnlockEventDetail) => void): () => void {
  const handler = (ev: Event) => cb((ev as CustomEvent<MediaUnlockEventDetail>).detail);
  window.addEventListener('sc:media-unlocked', handler as EventListener);
  return () => window.removeEventListener('sc:media-unlocked', handler as EventListener);
}

export default MediaUnlock;
